// External libraries
import React from 'react';
import Loadable from 'react-loadable';
import { isNumber } from '@checkout/react-components';

// Internal modules
import { isFunction } from '@/lib/utils/typeUtils';
import { fallback } from './redirects';
import { logger } from '../logger';

import { EVENTS, PAGE, triggerInitialCPLEvent } from '@/lib/logger/cplUtils';

const reportCplOnLazyComponentsLoaded = () => {
  triggerInitialCPLEvent(PAGE.LANDING, EVENTS.LAZY_COMPONENTS_LOADED);
};

/**
 * @function getLazyLoaderRenderFunction
 * @param {object} options | { fptiTrackName, getComponentName componentName }
 *  fptiTrackName: used when tracking name for FPTI page load is different
 *    than the component name
 *  getComponentName: an optional function that will return the component name
 *  componentName: the named export that is to be loaded and rendered
 * @returns {func}
 */
const getLazyLoaderRenderFunction = options => {
  const { fptiTrackName, getComponentName, componentName } = options;
  return function render(loaded, props) {
    const exportName = isFunction(getComponentName)
      ? getComponentName()
      : componentName;
    if (!this.isLoaded) {
      this.isLoaded = true;
      logger.fptiTrackPageLoad.handleItemLoaded(fptiTrackName || exportName);
    }

    const Component = loaded[exportName];
    if (!Component) {
      logger.error('weasley_component_not_found', { component: exportName });
    }

    return <Component {...props} />;
  };
};

const componentsToBeLoaded = new Set();
const setComponentAsLoading = name => componentsToBeLoaded.add(name);
const setComponentAsLoaded = name => componentsToBeLoaded.delete(name);
const hasLoadedAllComponents = () => componentsToBeLoaded.size < 1;

/**
 * @function lazyLoader
 * @param {object} props | same as object passed to `Loadable()` plus
 * a `name` prop to log the name of the component on an error
 * @returns {func}
 * @description Returns a react-loadable `Loadable` component
 * that will fallback to xoon if a loading error occurs
 */
const lazyLoader = ({ loader, loading, name, ...rest }) => {
  const options = {};

  if (isFunction(rest.render)) {
    options.render = (loaded, props) => {
      setComponentAsLoaded(name);
      if (hasLoadedAllComponents()) {
        reportCplOnLazyComponentsLoaded();
      }
      return rest.render(loaded, props);
    };
  }

  if (isNumber(rest.delay)) {
    options.delay = rest.delay;
  }

  if (isNumber(rest.timeout)) {
    options.timeout = rest.timeout;
  }

  return Loadable({
    loader,
    loading: props => {
      setComponentAsLoading(name);
      if (props.error) {
        logger.error(`${name}_component_failed_to_load`, props.error);
        fallback({ reason: `${name}_component_failed_to_load` });

        // React wants a component returned, otherwise it freaks out...
        return React.createElement('p');
      }

      return loading(props);
    },
    ...options,
  });
};

export { getLazyLoaderRenderFunction, lazyLoader };
